import { styled } from '@mui/material';

import { Link } from 'react-router';

import { Typography, Button, Skeleton } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';
import { PeopleIconBig, RobotIconBig } from '@app-velocity/icons';

import { InfoSection } from '../../../../components/InfoSection';
import { useReportContext } from '../../../../context/useReportContext';

import { ReportPaths } from '../../../../paths/ReportPaths';

const Participants = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(8),
  justifyContent: 'center',
  padding: theme.spacing(8),
}));

const Participant = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  gap: theme.spacing(4),
  color: 'var(--text-text-primary-color)',
  '&:link,&:visited,&:hover,&:active': {
    color: 'var(--text-text-primary-color)',
  },
}));

const ParticipantValue = styled('div')(({ theme }) => ({
  fontSize: 53,
  fontWeight: 600,
}));

const getFill = (success: boolean, loading: boolean) => {
  if (loading) return 'var(--input-input-background-color)';
  if (success) return '#4BC840';

  return '#FF9090';
};

const ParticipantIcon = styled('div')<{ success: boolean; loading: boolean }>(
  ({ theme, loading, success }) => ({
    display: 'flex',
    svg: {
      height: theme.spacing(10),
      width: 'auto',
      fill: getFill(success, loading),
    },
  })
);

const ParticipantTop = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
}));
const ParticipantDescription = styled('div')({
  textAlign: 'center',
});

const RequiredParticipants = styled(Typography)({
  fontSize: 13,
  color: '#969696',
});

export const TestParticipants = () => {
  const { config, reportConfig, statistics, baseURL, isLoading } =
    useReportContext();

  const { uniqueHumanTesters, simulatedReviews } = statistics;
  const { humanParticipantsAllowed, hasAccessToSims } = config;

  const totalSimulatedReviews =
    simulatedReviews.failed + simulatedReviews.succeded;
  const { t } = useI18n();

  const { config: reportMetrics } = reportConfig;

  return (
    <InfoSection tooltip="prototypes:report.test_participants.tooltip">
      <InfoSection.Title title="prototypes:report.test_participants.title" />
      <Participants>
        {humanParticipantsAllowed &&
        typeof uniqueHumanTesters !== 'undefined' ? (
          <Participant to={ReportPaths.goToHumanParticipants(baseURL)}>
            <ParticipantTop>
              <ParticipantValue>
                {statistics.uniqueHumanTesters}
              </ParticipantValue>
              <ParticipantIcon
                loading={isLoading}
                success={
                  uniqueHumanTesters >= reportMetrics.minimumHumanParticipants
                }
              >
                <PeopleIconBig />
              </ParticipantIcon>
            </ParticipantTop>
            <ParticipantDescription>
              <Typography>
                {t('prototypes:report.test_participants.human')}
              </Typography>
              {isLoading ? (
                <Skeleton margin="0" width={69} height={19} />
              ) : (
                <RequiredParticipants>
                  {t('prototypes:report.test_participants.required', {
                    count: reportMetrics.minimumHumanParticipants,
                  })}
                </RequiredParticipants>
              )}
            </ParticipantDescription>
          </Participant>
        ) : null}
        <Participant to={ReportPaths.goToSimulatedParticipants(baseURL)}>
          <ParticipantTop>
            <ParticipantValue>{totalSimulatedReviews}</ParticipantValue>
            <ParticipantIcon
              loading={isLoading}
              success={
                totalSimulatedReviews >= reportMetrics.minimumAiParticipants
              }
            >
              <RobotIconBig />
            </ParticipantIcon>
          </ParticipantTop>
          <ParticipantDescription>
            <Typography>
              {t('prototypes:report.test_participants.ai_simulated')}
            </Typography>
            {isLoading ? (
              <Skeleton margin="0" width={69} height={19} />
            ) : (
              <RequiredParticipants>
                {t('prototypes:report.test_participants.required', {
                  count: reportMetrics.minimumAiParticipants,
                })}
              </RequiredParticipants>
            )}
          </ParticipantDescription>
        </Participant>
      </Participants>
      {hasAccessToSims ? (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          component={Link}
          to={ReportPaths.goToConfidence(baseURL)}
        >
          {t('prototypes:participants.add')}
        </Button>
      ) : null}
    </InfoSection>
  );
};
